import { type CallStatusListItem, type CallStatusListQuery } from '@utazzo/shared';
import { UzStatusBadge } from '@utazzo/ui';
import React from 'react';

import { listCallStatuses } from '../call-statuses.api';
import { CallStatusFormDrawer } from '../components/CallStatusFormDrawer';
import {
  useBulkDeleteCallStatuses,
  useBulkSetCallStatusesStatus,
  useCallStatusesList,
  useDeleteCallStatus,
} from '../use-call-statuses';

import { type DataGridColumn, type DataGridFilter } from '@/components/data-grid';
import { ListScreen, type ListScreenColumns } from '@/components/list-screen';
import { useCurrentUser } from '@/features/auth/use-auth';

const STATUS_OPTIONS = [
  { value: '', label: 'Active + inactive' },
  { value: 'active', label: 'Active only' },
  { value: 'inactive', label: 'Inactive only' },
] as const;

/** Server search only fires from this many characters. */
const MIN_SEARCH_LENGTH = 3;

// `Connected` was previously a per-row column + filter; the
// CallSubStatus master now carries the granular outcome (Reached,
// Voicemail, Busy, …) underneath each parent, so the dedicated
// connected/not-connected column on the parent table was redundant
// and has been retired.
const FILTERS: ReadonlyArray<DataGridFilter> = [
  {
    id: 'status',
    label: 'Status',
    options: STATUS_OPTIONS as ReadonlyArray<{ value: string; label: string }>,
    widthClass: 'w-48',
  },
];

export const CallStatusesListPage = (): React.JSX.Element => {
  const me = useCurrentUser();
  const canDelete = me?.role === 'ADMIN';
  const deleteCallStatus = useDeleteCallStatus();
  const bulkDeleteMut = useBulkDeleteCallStatuses();
  const bulkSetStatusMut = useBulkSetCallStatusesStatus();

  const columns: ListScreenColumns<CallStatusListItem> = ({ state }) => {
    const cols: ReadonlyArray<DataGridColumn<CallStatusListItem>> = [
      {
        id: 'serial',
        header: 'S.No',
        width: '64px',
        cell: (_row, index) => (
          <span className="text-zinc-500">{(state.page - 1) * state.pageSize + index + 1}.</span>
        ),
      },
      {
        id: 'name',
        header: 'Name',
        sortable: true,
        cell: (row) => (
          <div className="flex flex-col">
            <span className="font-medium text-zinc-900">{row.name}</span>
            <span className="text-xs text-zinc-500">{row.code}</span>
          </div>
        ),
      },
      {
        id: 'description',
        header: 'Description',
        cell: (row) => <span className="text-zinc-600">{row.description ?? '-'}</span>,
      },
      {
        id: 'sortOrder',
        header: 'Sort',
        sortable: true,
        width: '80px',
        cell: (row) => <span>{row.sortOrder}</span>,
      },
      {
        id: 'status',
        header: 'Status',
        cell: (row) => <UzStatusBadge status={row.isActive ? 'active' : 'inactive'} />,
      },
    ];
    return cols;
  };

  return (
    <ListScreen<CallStatusListItem, CallStatusListQuery>
      title="Call Statuses"
      description="Outcomes of follow-up calls (e.g. answered, voicemail, busy)."
      useList={useCallStatusesList}
      defaultSort={{ by: 'sortOrder', dir: 'asc' }}
      minSearchLength={MIN_SEARCH_LENGTH}
      toQuery={(state, base) => {
        const status = state.filters.status;
        return {
          ...base,
          ...(status === 'active' ? { isActive: true } : {}),
          ...(status === 'inactive' ? { isActive: false } : {}),
        };
      }}
      columns={columns}
      filters={FILTERS}
      getRowId={(row) => row.id}
      createButton={{ label: 'Add call status' }}
      renderDrawer={({ open, mode, row, close }) => (
        <CallStatusFormDrawer
          open={open}
          onOpenChange={(o) => {
            if (!o) close();
          }}
          mode={mode}
          callStatus={row}
        />
      )}
      bulkActions={{
        delete: {
          onDelete: async (rows) => {
            await bulkDeleteMut.mutateAsync(rows.map((r) => r.id));
          },
          confirm: (rows) => ({
            title: `Delete ${rows.length} call status${rows.length === 1 ? '' : 'es'}?`,
            description:
              'These will be soft-deleted and hidden from lists. Follow-up calls already recorded with them are unaffected.',
          }),
        },
        setActive: {
          onSetActive: async (rows, isActive) =>
            bulkSetStatusMut.mutateAsync({ ids: rows.map((r) => r.id), isActive }),
          confirm: (rows, isActive) => ({
            title: `${isActive ? 'Activate' : 'Deactivate'} ${rows.length} call status${rows.length === 1 ? '' : 'es'}?`,
            description: isActive
              ? 'They will appear in pickers again.'
              : 'They will be hidden from pickers but remain on historical records.',
          }),
        },
      }}
      export={{
        fetchPage: (q) => listCallStatuses(q),
        filename: 'call-statuses',
        csv: [
          { header: 'Code', value: (r) => r.code },
          { header: 'Name', value: (r) => r.name },
          { header: 'Description', value: (r) => r.description ?? '' },
          { header: 'Sort order', value: (r) => r.sortOrder },
          { header: 'Status', value: (r) => (r.isActive ? 'Active' : 'Inactive') },
          { header: 'Created at', value: (r) => r.createdAt },
        ],
      }}
      rowActions={{
        edit: true,
        delete: {
          onDelete: (row) => deleteCallStatus.mutateAsync(row.id),
          disabled: () => !canDelete,
          confirm: (row) => ({
            title: 'Delete this call status?',
            description: `${row.name} will be soft-deleted and hidden from lists. Follow-up calls already recorded with it are unaffected.`,
          }),
        },
      }}
      copy={{
        searchPlaceholder: 'Search by name or code (min 3 characters)',
        emptyTitle: 'No call statuses match these filters',
        emptyDescription: 'Adjust the status filter or clear the search box.',
        errorTitle: 'Failed to load call statuses',
        errorDescription: 'The server returned an error. Try refreshing or check your network.',
      }}
    />
  );
};
